import { useState } from 'react';
import { Box, Group, Button, Drawer, BackgroundImage, Image, Title, Text, Stack, em } from '@mantine/core';
import { IconTicketSvg } from '../../common/icons/icon-ticket';
import { IconMapPin } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { useSelector } from 'react-redux';
import { selectHeroContent } from '../../../redux/selectors/publicSelector';
import DetailsModal from '../details/details.modal.tsx';
import videoSrc from '/assets/videos/output2.mp4';
import drawerImage1 from "/assets/images/feb_images/image_5_red.jpg";
import drawerImage2 from "/assets/images/feb_images/image_3_red.jpg";
import drawerImage3 from "/assets/images/feb_images/image_6.jpeg";
import colors from '../../../utils/styles';
import './hero.section.css';
import parse from 'html-react-parser';

interface DrawerImageData {
  image: string;
  title: string;
  modalType: string;
  description: any;
}

interface IHeroSectionProps {
  scrollToCalendar: () => void;
  scrollToMap: () => void;
}

const HeroSection = ({ scrollToCalendar, scrollToMap }: IHeroSectionProps) => {
  const isTablet = useMediaQuery(`(max-width: ${em(1024)})`);
  const isMobile = useMediaQuery(`(max-width: ${em(520)})`);

  const { hero, buttons } = useSelector(selectHeroContent);

  const drawerImageData: DrawerImageData[] = [
    {
      image: drawerImage1,
      title: hero.experience.title,
      modalType: 'drawer-left-info',
      description: (
        <Stack gap="md">
          <Text>{parse(hero.experience.description || '')}</Text>

          <Stack gap="xs">
            {hero.experience.data.map((item, index) => (
              <Stack key={index} gap="xs">
                <Text fw={700}>{item.title}</Text>
                <Text>{parse(item.description || '')}</Text>
              </Stack>
            ))}
          </Stack>

          <Text fw={700}>{parse(hero.experience.closing || '')}</Text>
        </Stack>
      ),
    },
    {
      image: drawerImage2,
      title: hero.story.title,
      modalType: 'drawer-center-info',
      description: (
        <Stack gap="md">
          <Text>{parse(hero.story.description || '')}</Text>

          <Stack gap="xs">
            {hero.story.data.map((item, index) => (
              <Stack key={index} gap="xs">
                <Text fw={700}>{item.title}</Text>
                <Text>{parse(item.description || '')}</Text>
              </Stack>
            ))}
          </Stack>

          <Text>{parse(hero.story.closing || '')}</Text>
        </Stack>
      ),
    },
    {
      image: drawerImage3,
      title: hero.music.title,
      modalType: 'drawer-right-info',
      description: (
        <Stack gap="md">
          <Stack gap="xs">
            {hero.music.data.map((item, index) => (
              <Stack key={index} gap="xs">
                <Text fw={700}>{item.title}</Text>
                <Text>{parse(item.description || '')}</Text>
              </Stack>
            ))}
          </Stack>

          <Text>{parse(hero.music.closing || '')}</Text>
        </Stack>
      ),
    },
  ];

  const [experienceDrawerOpened, setExperienceDrawerOpened] = useState(false);
  const [modalDrawerData, setModalDrawerData] = useState<DrawerImageData | null>(null);

  const onModalClose = () => {
    setModalDrawerData(null);
  }

  return (
    <>
      {/* Video as first element with smoky gradients */}
      <Box style={{
        width: '100%',
        height: isTablet ? '75vh' : '44.55vw',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Video element */}
        <video
          className="react-player"
          src={videoSrc}
          autoPlay
          playsInline
          muted
          loop
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {/* Left side gradient */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, rgba(0,0,0,0) 94%, rgba(0,0,0,0.75) 100%)',
            zIndex: 2,
          }}
        />

        {/* Right side gradient */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(270deg, rgba(0,0,0,0) 94%, rgba(0,0,0,0.75) 100%)',
            zIndex: 2,
          }}
        />

        {/* Bottom gradient */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 94%, rgba(0,0,0,0.75) 100%)',
            zIndex: 2,
          }}
        />
      </Box>

      {/* Top navigation and logo - comes after video but positioned fixed */}
      <Box
        style={{
          position: "fixed",
          width: "100%",
          top: 0,
          left: 0,
          display: "flex",
          alignItems: "flex-start",
          background: "linear-gradient(0deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%)",
          zIndex: 205,
          height: '5rem',
        }}
      >
        {/* Logo — absolutely centred, standalone, non-interactive */}
        {!isTablet && <Image
          src="/assets/images/logo.png"
          alt="logo"
          fit="contain"
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            transform: 'translateX(-50%)',
            width: 'clamp(110px, 130vw, 150px)',
            height: 'auto',
            pointerEvents: 'none',
          }}
        />}

        <Group style={{ width: '100%', alignItems: 'flex-start' }}>
          {!isTablet
            ? <>
              <Box style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', paddingTop: '20px', paddingLeft: '5%' }}>
                <Button variant="transparent" color="white" size="sm" onClick={() => setExperienceDrawerOpened(!experienceDrawerOpened)}>
                  {buttons.hero.experience} {experienceDrawerOpened ? '-' : '+'}
                </Button>
                <Button variant="transparent" color="white" size="sm" onClick={scrollToMap}>
                  <IconMapPin size={16} style={{ marginRight: '4px' }} />{buttons.hero.location}
                </Button>
              </Box>
              <Box style={{ flex: 1 }} />
            </>
            : <>
              <Group justify="space-evenly" align="center" w="100%" wrap="nowrap">
                <Button variant="transparent" color="white" fz={22} onClick={() => setExperienceDrawerOpened(!experienceDrawerOpened)}>{experienceDrawerOpened ? '-' : '+'}</Button>
                <Stack gap={0} mt={10}>
                  <Text fw={700} c={colors.appYellow} fz={18}>{hero.title}</Text>
                  <Text c={colors.appYellow} fz={16} style={{ fontFamily: 'Playfair Display' }}>{hero.subtitle}</Text>
                </Stack>
                <Button variant="transparent" color="white" onClick={scrollToMap}> <IconMapPin size={22} stroke={2.5} /></Button>
              </Group>
            </>}
          <Box className="book-tickets-container">
            <Button
              size="sm"
              radius="xl"
              leftSection={<IconTicketSvg />}
              onClick={scrollToCalendar}
              style={{ backgroundColor: colors.appPink, height: '45px' }}
              className="book-tickets-button"
            >
              {buttons.hero.bookTickets}
            </Button>
          </Box>
        </Group>
      </Box>

      {/* Experience drawer */}
      <Drawer
        opened={experienceDrawerOpened}
        onClose={() => setExperienceDrawerOpened(false)}
        position="top"
        withCloseButton={false}
        transitionProps={{ duration: 350, timingFunction: 'linear' }}
        overlayProps={{ backgroundOpacity: 0, blur: 0 }}
        size="xs"
        lockScroll={false}
        styles={{
          content: {
            backgroundColor: colors.appDarkestBlue,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
            maxHeight: isTablet ? '250px' : 'none'
          },
          header: {
            backgroundColor: colors.appDarkestBlue,
          }
        }}
      >
        <Group
          justify="space-evenly"
          mt={isTablet ? "80" : "120"}
          onMouseLeave={() => {
            if (!modalDrawerData) {
              setExperienceDrawerOpened(false)
            }
          }}>
          {drawerImageData.map((data, index) => (
            <Box
              key={index}
              onClick={() => setModalDrawerData(data)}
              style={{ width: '30%' }}
            >
              <BackgroundImage
                src={data.image}
                radius="md"
                style={{
                  height: isMobile ? '110px' : isTablet ? '130px' : '160px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: isTablet ? '5px' : '20px',
                }}
              >
                <Title
                  classNames={{ root: 'drawer-title' }}
                  order={isTablet ? 6 : 2}
                >
                  {data.title}
                </Title>
              </BackgroundImage>
            </Box>
          ))}
        </Group>
      </Drawer>

      {modalDrawerData && <DetailsModal
        opened={!!modalDrawerData}
        onClose={onModalClose}
        title={modalDrawerData.title}
        description={modalDrawerData.description}
        image={modalDrawerData.image}
      />}
    </>
  );
};

export default HeroSection;